import React, { useState } from 'react';

const IOSInstallInstructions = ({ onClose, onGoToMap }) => {
  const [showInstructions, setShowInstructions] = useState(false);

  // Step 1: Consent Screen
  if (!showInstructions) {
    return (
      <>
        {/* Overlay */}
        <div className="fixed inset-0 bg-black/50 z-[60]" onClick={onClose} />
        
        {/* Modal */}
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl relative pointer-events-auto">
            {/* Orange X Button */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold cursor-pointer transition-all duration-200 hover:scale-110"
              style={{
                background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                boxShadow: '0 3px 10px rgba(245, 158, 11, 0.4)',
              }}
            >
              ×
            </button>

            {/* Happy Paws Icon */}
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center mb-4">
                <svg className="w-20 h-20" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="28" cy="28" r="26" fill="white" stroke="#10B981" strokeWidth="3"/>
                  <image href="/icons/paw-frameless-64.png" x="8" y="8" width="40" height="40" />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
              Install Happy Paws on Your iPhone?
            </h3>

            {/* Benefits */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-emerald-900 mb-3 text-sm">✨ Benefits:</h4>
              <ul className="space-y-2 text-sm text-emerald-800">
                <li className="flex items-start gap-2">
                  <span>🚀</span>
                  <p>Works like a native app</p>
                </li>
                <li className="flex items-start gap-2">
                  <span>📱</span>
                  <p>Home screen icon access</p>
                </li>
                <li className="flex items-start gap-2">
                  <span>⚡</span>
                  <p>Faster loading times</p>
                </li>
                <li className="flex items-start gap-2">
                  <span>🔒</span>
                  <p>Better privacy & security</p>
                </li>
              </ul>
            </div>

            {/* Simple explanation */}
            <p className="text-gray-600 text-sm text-center mb-6 leading-relaxed">
              Installation takes about 30 seconds and we'll guide you through each step.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => setShowInstructions(true)}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white py-3 px-4 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                Yes, show me how! 🎉
              </button>
              <button
                onClick={onGoToMap}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white py-3 px-4 rounded-full font-semibold shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                Maybe later - go to map
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Step 2: Persistent Instructions Screen (Cannot be closed except by refresh)
  return (
    <>
      {/* Persistent Overlay (no onClick to prevent closing) */}
      <div className="fixed inset-0 bg-black/70 z-[60]" />
      
      {/* Persistent Modal */}
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none overflow-y-auto">
        <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl relative pointer-events-auto my-8">
          {/* NO CLOSE BUTTON - Persistent until refresh */}
          
          {/* Happy Paws Icon */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center justify-center mb-4">
              <svg className="w-20 h-20" viewBox="0 0 56 56" xmlns="http://www.w3.org/2000/svg">
                <circle cx="28" cy="28" r="26" fill="white" stroke="#10B981" strokeWidth="3"/>
                <image href="/icons/paw-frameless-64.png" x="8" y="8" width="40" height="40" />
              </svg>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">
            📱 iPhone Installation Guide
          </h3>

          {/* Persistent Notice */}
          <div className="bg-blue-100 border-2 border-blue-300 rounded-lg p-3 mb-4">
            <p className="text-xs text-blue-900 text-center font-medium">
              💡 These instructions will stay on screen while you install. Follow along at your own pace!
            </p>
          </div>

          {/* Installation Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-blue-900 mb-4 text-sm">Follow these steps:</h4>
            <ol className="space-y-4 text-sm text-blue-900">
              <li className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                <span className="font-bold text-lg text-emerald-600">1.</span>
                <div>
                  <p className="font-semibold mb-1">Tap the Share button</p>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-200 rounded">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 5v10m0 0l3-3m-3 3l-3-3M5 19h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <span className="text-xs text-gray-600">at the bottom of Safari</span>
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                <span className="font-bold text-lg text-emerald-600">2.</span>
                <div>
                  <p className="font-semibold mb-1">Scroll down and tap</p>
                  <div className="inline-flex items-center gap-1 px-3 py-1 bg-blue-200 rounded-full text-xs font-semibold">
                    ➕🏠 "Add to Home Screen"
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                <span className="font-bold text-lg text-emerald-600">3.</span>
                <div>
                  <p className="font-semibold mb-1">Tap "Add"</p>
                  <span className="text-xs text-gray-600">in the top right corner</span>
                </div>
              </li>
              <li className="flex items-start gap-3 p-3 bg-white rounded-lg shadow-sm">
                <span className="font-bold text-lg text-emerald-600">4.</span>
                <div>
                  <p className="font-semibold mb-1">Find the Happy Paws icon! 🐾</p>
                  <span className="text-xs text-gray-600">Look on your home screen</span>
                </div>
              </li>
            </ol>
          </div>

          {/* Pro Tip */}
          <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-xs text-emerald-800 text-center">
              ✨ <strong>Tip:</strong> Once installed, Happy Paws works offline and loads instantly!
            </p>
          </div>

          {/* Refresh Notice */}
          <div className="mt-3 p-2 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              These instructions will remain visible until you refresh the page.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default IOSInstallInstructions;
